import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api';

const GARNET = '#5b1e22';
const COAL = '#181617';
const ASHEN = '#7A766F';
const OYSTER = '#F7F7F7';

const TRIGGER_LABELS = {
  booking_created: 'Booking Confirmation',
  booking_reminder_day_before: 'Reminder (Day Before)',
  booking_reminder_day_of: 'Reminder (Day Of)',
  piercing_logged: 'Piercing Logged',
  day7_healing: 'Day 7 Healing',
  day30_downsize: 'Day 30 Downsize',
  downsize_confirmed: 'Downsize Confirmed',
  downsize_not_ready: 'Downsize Not Ready',
  checkup_logged: 'Checkup Logged',
  consultation_acknowledged: 'Consultation Follow-up',
  no_show: 'No-Show Follow-up',
  purchase_synced: 'Post-Purchase',
  birthday: 'Birthday',
  month6_nudge: '6-Month Nudge',
  downsize_recheck: 'Recheck',
  manual: 'Manual',
  whatsapp_ai_reply: 'AI Reply',
  whatsapp_inbound: 'WhatsApp'
};

const TRIGGER_COLORS = {
  booking_created: { bg: '#e8f5ef', color: '#1a7a4c' },
  booking_reminder_day_before: { bg: '#e8f0f5', color: '#1a5a7a' },
  booking_reminder_day_of: { bg: '#e8f0f5', color: '#1a5a7a' },
  piercing_logged: { bg: '#e8f5ef', color: '#1a7a4c' },
  day7_healing: { bg: '#e8f5ef', color: '#1a7a4c' },
  day30_downsize: { bg: '#e8f0f5', color: '#1a5a7a' },
  downsize_confirmed: { bg: '#e8f5ef', color: '#1a7a4c' },
  downsize_not_ready: { bg: '#fff3e6', color: '#b35c00' },
  checkup_logged: { bg: '#fff3e6', color: '#b35c00' },
  consultation_acknowledged: { bg: '#f3eef8', color: '#6b4c8a' },
  no_show: { bg: '#fce8e8', color: '#b32d2d' },
  purchase_synced: { bg: '#f3eef8', color: '#6b4c8a' },
  birthday: { bg: '#fce8e8', color: '#b32d2d' },
  month6_nudge: { bg: '#f3eef8', color: '#6b4c8a' },
  downsize_recheck: { bg: '#fff3e6', color: '#b35c00' },
  manual: { bg: '#f3f4f6', color: '#374151' },
  whatsapp_ai_reply: { bg: '#e8f0f5', color: '#1a5a7a' },
  whatsapp_inbound: { bg: '#f3eef8', color: '#6b4c8a' }
};

function TriggerBadge({ trigger }) {
  const label = TRIGGER_LABELS[trigger] || trigger;
  const colors = TRIGGER_COLORS[trigger] || { bg: '#f3f4f6', color: '#6b7280' };
  return (
    <span style={{ fontSize: '0.65rem', padding: '0.15rem 0.45rem', borderRadius: 8, fontWeight: 600, background: colors.bg, color: colors.color }}>
      {label}
    </span>
  );
}

function StatusDot({ status }) {
  const colors = { drafted: '#b35c00', approved: '#1a7a4c', sent: '#1a5a7a', failed: '#b32d2d', received: '#6b4c8a' };
  return <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: colors[status] || '#ccc', marginRight: 4 }} />;
}

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const yesterday = new Date(now); yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function formatFullTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function formatDate(iso) {
  if (!iso) return '---';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
function MediaImage({ messageId }) {
  const [src, setSrc] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    api.get(`/messages/media/${messageId}`, { responseType: 'blob' })
      .then(res => {
        if (!cancelled) setSrc(URL.createObjectURL(res.data));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [messageId]);
  if (!src) return null;
  return (
    <img
      src={src}
      alt=""
      style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 8, marginBottom: 6, cursor: 'pointer' }}
      onClick={() => window.open(src, '_blank')}
    />
  );
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

// ─── Conversation List ──────────────────────────────────────────────
function ConversationList({ conversations, selectedId, onSelect, filter, onFilterChange, countryFilter, onCountryFilterChange, isMobile, searchQuery, onSearchChange }) {
  return (
    <div style={{
      borderRight: isMobile ? 'none' : '1px solid #e5e2de',
      height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0
    }}>
      <div style={{ padding: '0.75rem', borderBottom: '1px solid #e5e2de', flexShrink: 0 }}>
        <div style={{ position: 'relative', marginBottom: searchQuery ? 0 : 8 }}>
          <input
            type="text"
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            placeholder="Search by name or message..."
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '0.45rem 2rem 0.45rem 0.75rem',
              border: '1px solid #e5e2de', borderRadius: 6,
              fontSize: '0.8rem', outline: 'none',
              background: searchQuery ? '#fff' : '#f9f9f9',
              color: COAL
            }}
          />
          {searchQuery && (
            <button onClick={() => onSearchChange('')}
              style={{
                position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer',
                color: ASHEN, fontSize: '1.1rem', lineHeight: 1, padding: 0
              }}>×</button>
          )}
        </div>
        {!searchQuery && (
          <>
            <div style={{ display: 'flex', gap: 4, background: '#f3f4f6', borderRadius: 6, padding: 2, marginBottom: 6 }}>
              {[
                { key: 'all', label: 'All' },
                { key: 'HK', label: 'HK' },
                { key: 'SG', label: 'SG' },
              ].map(f => (
                <button key={f.key} onClick={() => onCountryFilterChange(f.key)} style={{
                  flex: 1, padding: '0.35rem 0.5rem', border: 'none', borderRadius: 5,
                  fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                  background: countryFilter === f.key ? '#fff' : 'transparent',
                  color: countryFilter === f.key ? COAL : ASHEN,
                  boxShadow: countryFilter === f.key ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                  minHeight: 30
                }}>{f.label}</button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 4, background: '#f3f4f6', borderRadius: 6, padding: 2 }}>
              {[
                { key: 'all', label: 'All' },
                { key: 'drafts', label: 'Drafts' },
                { key: 'needs_reply', label: 'Needs Reply' }
              ].map(f => (
                <button key={f.key} onClick={() => onFilterChange(f.key)} style={{
                  flex: 1, padding: '0.4rem 0.5rem', border: 'none', borderRadius: 5,
                  fontSize: '0.8rem', fontWeight: 500, cursor: 'pointer',
                  background: filter === f.key ? '#fff' : 'transparent',
                  color: filter === f.key ? COAL : ASHEN,
                  boxShadow: filter === f.key ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                  minHeight: 36
                }}>{f.label}</button>
              ))}
            </div>
          </>
        )}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, WebkitOverflowScrolling: 'touch' }}>
        {conversations.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem 1rem', color: ASHEN, fontSize: '0.85rem' }}>
            {searchQuery ? 'No results found' : 'No conversations yet'}
          </div>
        ) : conversations.map(c => (
          <div key={c.customer_id} onClick={() => onSelect(c.customer_id)}
            style={{
              padding: '0.85rem 1rem', borderBottom: '1px solid #f3f4f6', cursor: 'pointer',
              background: !isMobile && selectedId === c.customer_id ? '#f9f0f1' : 'transparent',
              transition: 'background 0.15s'
            }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flex: 1 }}>
                <span style={{ fontWeight: 600, fontSize: '0.9rem', color: COAL, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                {c.muted && (
                  <span style={{ fontSize: '0.6rem', padding: '0.1rem 0.35rem', borderRadius: 6, background: '#f3f4f6', color: ASHEN, fontWeight: 600, flexShrink: 0 }}>
                    Muted
                  </span>
                )}
              </div>
              <span style={{ fontSize: '0.7rem', color: ASHEN, flexShrink: 0, marginLeft: 8 }}>{formatTime(c.last_message_at)}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 3 }}>
              {c.last_direction === 'outbound' && <span style={{ fontSize: '0.7rem', color: ASHEN, flexShrink: 0 }}>You:</span>}
              <span style={{ fontSize: '0.8rem', color: ASHEN, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                {c.last_message}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 4, marginTop: 4, alignItems: 'center', flexWrap: 'wrap' }}>
              {c.draft_count > 0 && (
                <span style={{ fontSize: '0.6rem', padding: '0.1rem 0.35rem', borderRadius: 6, background: '#fff3e6', color: '#b35c00', fontWeight: 600 }}>
                  {c.draft_count} draft{c.draft_count > 1 ? 's' : ''}
                </span>
              )}
              {c.needs_reply && (
                <span style={{ fontSize: '0.6rem', padding: '0.1rem 0.35rem', borderRadius: 6, background: '#f3eef8', color: '#6b4c8a', fontWeight: 600 }}>
                  Needs reply
                </span>
              )}
              {c.is_automated && c.trigger_type && <TriggerBadge trigger={c.trigger_type} />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Message Thread ─────────────────────────────────────────────────
function MessageThread({ customerId, onMessageChange, onBack, isMobile }) {
  const navigate = useNavigate();
  const [thread, setThread] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState('');
  const [composeText, setComposeText] = useState('');
  const [composing, setComposing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [muteLoading, setMuteLoading] = useState(false);
  const scrollContainerRef = useRef(null);
  const isInitialLoad = useRef(true);
  const prevMessageCount = useRef(0);

  const loadThread = useCallback(async () => {
    try {
      const res = await api.get(`/messages/thread/${customerId}`);
      setThread(res.data);
    } catch (e) {
      console.error('Failed to load thread:', e);
    }
  }, [customerId]);

  useEffect(() => {
    isInitialLoad.current = true;
    prevMessageCount.current = 0;
    setThread(null);
    loadThread();
  }, [loadThread]);

  // Auto-refresh thread every 10 seconds
  useEffect(() => {
    const interval = setInterval(loadThread, 10000);
    return () => clearInterval(interval);
  }, [loadThread]);

  // Scroll logic: only scroll the messages container, never the page
  useEffect(() => {
    if (!thread || !scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const messageCount = thread.messages.length;

    if (isInitialLoad.current) {
      // First load or conversation switch: scroll container to bottom
      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
      });
      isInitialLoad.current = false;
      prevMessageCount.current = messageCount;
      return;
    }

    if (messageCount > prevMessageCount.current) {
      // New message arrived — only auto-scroll if already near bottom
      const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      if (distanceFromBottom < 150) {
        requestAnimationFrame(() => {
          container.scrollTop = container.scrollHeight;
        });
      }
    }

    prevMessageCount.current = messageCount;
  }, [thread]);

  useEffect(() => {
    if (success) { const t = setTimeout(() => setSuccess(''), 3000); return () => clearTimeout(t); }
  }, [success]);

  const handleApprove = async (msgId) => {
    try {
      await api.post('/followups/approve', { message_id: msgId });
      setSuccess('Approved');
      loadThread();
      onMessageChange();
    } catch (e) { setError(e.response?.data?.error || 'Failed'); }
  };

  const handleSaveEdit = async (msgId) => {
    try {
      await api.put(`/messages/${msgId}/edit`, { body: editBody });
      setEditingId(null);
      setSuccess('Edit saved');
      loadThread();
      onMessageChange();
    } catch (e) { setError(e.response?.data?.error || 'Failed'); }
  };

  // One-tap skip reasons — mirrored in backend/message_review.py REJECT_CATEGORIES
  const REJECT_CATEGORIES = [
    ['wrong_info', 'Wrong info'], ['tone', 'Tone / voice'], ['too_long', 'Too long'],
    ['no_reply_needed', 'No reply needed'], ['stale', 'Customer sent more'],
    ['booking_mistake', 'Booking mistake'], ['missed_question', 'Missed the question'], ['other', 'Other'],
  ];
  const [skipPending, setSkipPending] = useState(null); // { msgId, category, reason }

  const handleSkipClick = (msgId) => {
    setSkipPending({ msgId, category: null, reason: '' });
  };

  const handleSkipConfirm = async () => {
    if (!skipPending || !skipPending.category) return;
    try {
      await api.post('/followups/skip', {
        message_id: skipPending.msgId,
        rejection_category: skipPending.category,
        rejection_reason: skipPending.reason || null
      });
      setSkipPending(null);
      setSuccess('Skipped');
      loadThread();
      onMessageChange();
    } catch (e) { setError(e.response?.data?.error || 'Failed'); }
  };

  const handleSkipFollowup = async (followupId) => {
    try {
      await api.post('/followups/skip-followup', { followup_id: followupId });
      setSuccess('Follow-up cancelled');
      loadThread();
      onMessageChange();
    } catch (e) { setError(e.response?.data?.error || 'Failed'); }
  };

  const handleSkipAllFollowups = async () => {
    if (!window.confirm('Cancel all pending follow-ups for this customer?')) return;
    try {
      await api.post(`/customers/${customerId}/skip-all-followups`);
      setSuccess('All follow-ups cancelled');
      loadThread();
      onMessageChange();
    } catch (e) { setError(e.response?.data?.error || 'Failed'); }
  };

  const handleMuteToggle = async () => {
    if (!thread) return;
    const isMuted = thread.customer.muted;
    const action = isMuted ? 'unmute' : 'mute';

    if (!isMuted && !window.confirm(`Mute ${thread.customer.name}? This will cancel all pending follow-ups and stop all future automated messages.`)) return;

    setMuteLoading(true);
    try {
      const res = await api.post(`/customers/${customerId}/${action}`);
      setSuccess(res.data.message);
      loadThread();
      onMessageChange();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed');
    }
    setMuteLoading(false);
  };
const handleDismissNeedsReply = async () => {
    try {
      await api.post(`/messages/dismiss-needs-reply`, { customer_id: customerId });
      setSuccess('Marked as no reply needed');
      loadThread();
      onMessageChange();
    } catch (e) { setError(e.response?.data?.error || 'Failed'); }
  };

  const handleCompose = async () => {
    if (!composeText.trim()) return;
    setComposing(true);
    try {
      await api.post('/messages/compose', { customer_id: customerId, body: composeText });
      setComposeText('');
      setSuccess('Message sent');
      loadThread();
      onMessageChange();
    } catch (e) { setError(e.response?.data?.error || 'Failed'); }
    setComposing(false);
  };

  if (!thread) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: ASHEN }}>Loading...</div>;

  const c = thread.customer;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Header */}
      <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e5e2de', background: '#fff', flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, minWidth: 0, flex: 1 }}>
            {isMobile && onBack && (
              <button
                onClick={onBack}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0',
                  fontSize: '1.2rem', color: GARNET, fontWeight: 700, flexShrink: 0, minHeight: 32, minWidth: 32,
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
              >
                ←
              </button>
            )}
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span
                  onClick={() => navigate(`/customers/${customerId}`, { state: { fromMessages: true, fromCustomerId: customerId } })}
                  style={{ fontWeight: 600, fontSize: '1rem', cursor: 'pointer', color: GARNET, textDecoration: 'underline', textDecorationColor: 'transparent' }}
                  onMouseEnter={e => e.target.style.textDecorationColor = GARNET}
                  onMouseLeave={e => e.target.style.textDecorationColor = 'transparent'}
                >{c.name}</span>
                {c.muted && (
                  <span style={{ fontSize: '0.65rem', padding: '0.15rem 0.45rem', borderRadius: 6, background: '#fce8e8', color: '#b32d2d', fontWeight: 600 }}>
                    Muted
                  </span>
                )}
              </div>
              <div style={{ fontSize: '0.75rem', color: ASHEN, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {c.whatsapp_number || c.phone || 'No number'}{c.email ? ` · ${c.email}` : ''}{` · ${c.country}`}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            {thread.needs_reply && (
              <button
                onClick={handleDismissNeedsReply}
                style={{
                  padding: '0.35rem 0.75rem', border: 'none', borderRadius: 6, fontSize: '0.75rem',
                  fontWeight: 600, cursor: 'pointer', minHeight: 36,
                  background: '#e8f0f5', color: '#1a5a7a'
                }}
              >
                No Reply Needed
              </button>
            )}
            <button
              onClick={handleMuteToggle}
              disabled={muteLoading}
              style={{
                padding: '0.35rem 0.75rem', border: 'none', borderRadius: 6, fontSize: '0.75rem',
                fontWeight: 600, cursor: 'pointer', minHeight: 36,
                background: c.muted ? '#e8f5ef' : '#fce8e8',
                color: c.muted ? '#1a7a4c' : '#b32d2d',
                opacity: muteLoading ? 0.4 : 1
              }}
            >
              {muteLoading ? '...' : (c.muted ? 'Unmute' : 'Mute')}
            </button>
          </div>
        </div>

        {/* Pending follow-ups */}
        {thread.pending_followups.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: '0.7rem', fontWeight: 600, color: ASHEN, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Upcoming follow-ups
              </span>
              {thread.pending_followups.length > 1 && (
                <button
                  onClick={handleSkipAllFollowups}
                  style={{
                    padding: '0.2rem 0.5rem', border: 'none', borderRadius: 4, fontSize: '0.65rem',
                    fontWeight: 600, cursor: 'pointer', background: '#fce8e8', color: '#b32d2d', minHeight: 28
                  }}
                >
                  Cancel all
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {thread.pending_followups.map(f => (
                <div key={f.id} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  fontSize: '0.65rem', padding: '0.2rem 0.5rem', borderRadius: 6,
                  background: '#e8f0f5', color: '#1a5a7a', fontWeight: 500
                }}>
                  <span>{TRIGGER_LABELS[f.trigger] || f.trigger}: {formatDate(f.scheduled_for)}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleSkipFollowup(f.id); }}
                    title="Cancel this follow-up"
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px',
                      fontSize: '0.85rem', color: '#1a5a7a', fontWeight: 700, lineHeight: 1, minHeight: 24, minWidth: 24,
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {error && <div style={{ padding: '0.5rem 1rem', background: '#fee2e2', color: '#991b1b', fontSize: '0.8rem', flexShrink: 0 }}>{error}</div>}
      {success && <div style={{ padding: '0.5rem 1rem', background: '#d1fae5', color: '#065f46', fontSize: '0.8rem', flexShrink: 0 }}>{success}</div>}

      {/* Messages — scrollable area */}
      <div
        ref={scrollContainerRef}
        style={{ flex: 1, overflowY: 'auto', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', minHeight: 0, WebkitOverflowScrolling: 'touch' }}
      >
        {thread.messages.length === 0 ? (
          <div style={{ textAlign: 'center', color: ASHEN, fontSize: '0.85rem', padding: '3rem 0' }}>No messages yet</div>
        ) : thread.messages.map(m => {
          const isOutbound = m.direction === 'outbound';
          const isDraft = m.status === 'drafted';
          const isApproved = m.status === 'approved';
          const isSent = m.status === 'sent';
          const isFailed = m.status === 'failed';

          return (
            <div key={m.id} style={{ display: 'flex', justifyContent: isOutbound ? 'flex-end' : 'flex-start' }}>
              <div style={{
                maxWidth: editingId === m.id ? '100%' : (isMobile ? '88%' : '75%'), borderRadius: 12, padding: '0.6rem 0.85rem',
                background: isOutbound
                  ? (isDraft ? '#fff3e6' : isApproved ? '#e8f5ef' : isSent ? '#e8f0f5' : isFailed ? '#fce8e8' : '#f3f4f6')
                  : '#f3f4f6',
                border: isDraft ? '1px dashed #b35c00' : '1px solid transparent'
              }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
                  <StatusDot status={m.status} />
                  <span style={{ fontSize: '0.65rem', color: ASHEN, textTransform: 'capitalize' }}>{m.status}</span>
                  {m.trigger_type && <TriggerBadge trigger={m.trigger_type} />}
                  {m.is_automated && <span style={{ fontSize: '0.6rem', color: ASHEN }}>Auto</span>}
                </div>

                {editingId === m.id ? (
                  /* Placeholder shown in the bubble while overlay is open */
                  <div style={{ fontSize: '0.75rem', color: '#b35c00', fontStyle: 'italic' }}>Editing…</div>
                ) : (
                  <>
                    {m.media_url && <MediaImage messageId={m.id} />}
                    {m.body?.startsWith('[No reply needed') ? (
                      <div style={{ fontSize: '0.75rem', lineHeight: 1.4, color: ASHEN, fontStyle: 'italic' }}>{m.body.replace(/^\[|\]$/g, '')}</div>
                    ) : (
                      <div style={{ fontSize: '0.85rem', lineHeight: 1.5, whiteSpace: 'pre-wrap', color: COAL }}>{m.body}</div>
                    )}
                    {m.translation && (
                      <div style={{
                        marginTop: '0.5rem', paddingTop: '0.5rem',
                        borderTop: '1px dashed #d1c8c0',
                        fontSize: '0.75rem', color: ASHEN, fontStyle: 'italic', lineHeight: 1.4
                      }}>
                        <span style={{ fontWeight: 600, fontStyle: 'normal' }}>EN: </span>{m.translation}
                      </div>
                    )}
                  </>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, flexWrap: 'wrap', gap: 4 }}>
                  <span style={{ fontSize: '0.65rem', color: ASHEN }}>
                    {formatFullTime(m.created_at)}
                    {m.approved_by_name ? ` · ${m.approved_by_name}` : ''}
                  </span>
                  {isDraft && editingId !== m.id && (
                    skipPending?.msgId === m.id ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4, width: '100%' }}>
                        {/* One-tap reason — required. This is what Lily learns from. */}
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {REJECT_CATEGORIES.map(([key, label]) => {
                            const on = skipPending.category === key;
                            return (
                              <button key={key} onClick={() => setSkipPending(p => ({ ...p, category: key }))} style={{
                                padding: '0.25rem 0.55rem', borderRadius: 999, fontSize: '0.68rem', fontWeight: 600, cursor: 'pointer',
                                border: `1px solid ${on ? '#b32d2d' : '#e5e7eb'}`, background: on ? '#b32d2d' : '#fff', color: on ? '#fff' : COAL
                              }}>{label}</button>
                            );
                          })}
                        </div>
                        <textarea
                          autoFocus
                          placeholder="Optional: paste the reply you'll send instead, or a note — Lily learns from it"
                          value={skipPending.reason}
                          onChange={e => setSkipPending(p => ({ ...p, reason: e.target.value }))}
                          rows={2}
                          style={{ width: '100%', fontSize: '0.75rem', padding: '0.4rem 0.5rem', borderRadius: 6, border: '1px solid #e5e7eb', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }}
                        />
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                          <button onClick={handleSkipConfirm} disabled={!skipPending.category} style={{ padding: '0.3rem 0.7rem', background: skipPending.category ? '#b32d2d' : '#e5e7eb', color: skipPending.category ? '#fff' : ASHEN, border: 'none', borderRadius: 6, fontSize: '0.7rem', fontWeight: 600, cursor: skipPending.category ? 'pointer' : 'not-allowed' }}>Confirm Skip</button>
                          <button onClick={() => setSkipPending(null)} style={{ padding: '0.3rem 0.7rem', background: '#f3f4f6', color: COAL, border: 'none', borderRadius: 6, fontSize: '0.7rem', cursor: 'pointer' }}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <button onClick={() => handleApprove(m.id)} style={{ padding: '0.3rem 0.6rem', background: '#1a7a4c', color: '#fff', border: 'none', borderRadius: 6, fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', minHeight: 32 }}>Approve</button>
                        <button onClick={() => { setEditingId(m.id); setEditBody(m.body); }} style={{ padding: '0.3rem 0.6rem', background: '#f3f4f6', color: COAL, border: 'none', borderRadius: 6, fontSize: '0.7rem', cursor: 'pointer', minHeight: 32 }}>Edit</button>
                        <button onClick={() => handleSkipClick(m.id)} style={{ padding: '0.3rem 0.6rem', background: '#fee2e2', color: '#b32d2d', border: 'none', borderRadius: 6, fontSize: '0.7rem', cursor: 'pointer', minHeight: 32 }}>Skip</button>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Compose — pinned to bottom */}
      <div style={{
        padding: '0.75rem 1rem',
        paddingBottom: isMobile ? 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' : '0.75rem',
        borderTop: '1px solid #e5e2de', background: '#fff', flexShrink: 0
      }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <textarea value={composeText} onChange={e => setComposeText(e.target.value)}
            placeholder="Type a message..." rows={2}
            style={{ flex: 1, padding: '0.6rem', border: '1px solid #e5e2de', borderRadius: 8, fontFamily: 'inherit', fontSize: '16px', resize: 'none' }}
          />
          <button onClick={handleCompose} disabled={composing || !composeText.trim()}
            style={{
              padding: '0.6rem 1rem', background: GARNET, color: '#fff', border: 'none',
              borderRadius: 8, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
              alignSelf: 'flex-end', opacity: composing || !composeText.trim() ? 0.4 : 1,
              minHeight: 44, minWidth: 60
            }}>
            {composing ? '...' : 'Send'}
          </button>
        </div>
        <div style={{ fontSize: '0.7rem', color: ASHEN, marginTop: 4 }}>
          Press Send to send your message
        </div>
      </div>

      {/* Edit overlay — bottom sheet on mobile, centred modal on desktop */}
      {editingId && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 300,
          background: 'rgba(0,0,0,0.35)',
          display: 'flex',
          alignItems: isMobile ? 'flex-end' : 'center',
          justifyContent: 'center',
          padding: isMobile ? 0 : '2rem'
        }}>
          <div style={{
            background: '#fff3e6',
            border: '1px dashed #b35c00',
            borderRadius: isMobile ? '16px 16px 0 0' : 12,
            padding: isMobile ? '1.25rem 1rem calc(1.25rem + env(safe-area-inset-bottom, 0px))' : '1.25rem',
            width: isMobile ? '100%' : '80vw',
            maxWidth: isMobile ? '100%' : 900,
            boxShadow: '0 -4px 24px rgba(0,0,0,0.15)'
          }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#b35c00', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Editing AI response
            </div>
            <textarea
              value={editBody}
              onChange={e => setEditBody(e.target.value)}
              autoFocus
              style={{
                width: '100%', minHeight: isMobile ? 160 : 220, padding: '0.85rem',
                border: '1px solid #e5c89a', borderRadius: 8,
                fontFamily: 'inherit', fontSize: isMobile ? '16px' : '0.95rem',
                resize: 'vertical', lineHeight: 1.6, boxSizing: 'border-box',
                background: '#fff'
              }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexDirection: isMobile ? 'column' : 'row' }}>
              <button
                onClick={() => handleSaveEdit(editingId)}
                style={{ padding: '0.6rem 1.1rem', background: '#b35c00', color: '#fff', border: 'none', borderRadius: 6, fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', minHeight: 44 }}
              >Save Edit</button>
              <button
                onClick={() => setEditingId(null)}
                style={{ padding: '0.6rem 1.1rem', background: '#f3f4f6', color: COAL, border: 'none', borderRadius: 6, fontSize: '0.9rem', cursor: 'pointer', minHeight: 44 }}
              >Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Template Editor Modal ──────────────────────────────────────────
function TemplateEditor({ template, triggerTypes, triggerVariables, studios, onSave, onClose }) {
  const isNew = !template;
  const isMobile = useIsMobile();
  const [name, setName] = useState(template?.name || '');
  const [body, setBody] = useState(template?.body || '');
  const [triggerType, setTriggerType] = useState(template?.trigger_type || '');
  const [timingValue, setTimingValue] = useState(template?.timing_value || 0);
  const [timingUnit, setTimingUnit] = useState(template?.timing_unit || 'immediately');
  const [timingDirection, setTimingDirection] = useState(template?.timing_direction || 'after');
  const [studioId, setStudioId] = useState(template?.studio_id || '');
  const [autoSend, setAutoSend] = useState(template?.auto_send || false);
  const [preview, setPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const availableVars = triggerVariables[triggerType] || [];

  const loadPreview = useCallback(async () => {
    if (!body.trim()) { setPreview(''); return; }
    try {
      const res = await api.post('/templates/preview', { body, trigger_type: triggerType });
      setPreview(res.data.preview);
    } catch (e) {
      setPreview('');
    }
  }, [body, triggerType]);

  useEffect(() => {
    const t = setTimeout(loadPreview, 500);
    return () => clearTimeout(t);
  }, [loadPreview]);

  const insertVariable = (varName) => {
    const textarea = document.getElementById('template-body-input');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newBody = body.substring(0, start) + `{${varName}}` + body.substring(end);
      setBody(newBody);
      setTimeout(() => {
        textarea.focus();
        const pos = start + varName.length + 2;
        textarea.setSelectionRange(pos, pos);
      }, 10);
    } else {
      setBody(body + `{${varName}}`);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) { setError('Template name is required'); return; }
    if (!body.trim()) { setError('Message body is required'); return; }
    if (!triggerType) { setError('Trigger type is required'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        name, body, trigger_type: triggerType,
        timing_value: timingUnit === 'immediately' ? 0 : parseInt(timingValue) || 0,
        timing_unit: timingUnit,
        timing_direction: timingDirection,
        studio_id: studioId || null,
        auto_send: autoSend,
        is_active: template?.is_active !== undefined ? template.is_active : true
      };
      if (isNew) {
        await api.post('/templates', payload);
      } else {
        await api.put(`/templates/${template.id}`, payload);
      }
      onSave();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save');
    }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : '2rem' }}>
      <div style={{
        background: '#fff', borderRadius: isMobile ? '16px 16px 0 0' : 16, width: '100%',
        maxWidth: isMobile ? '100%' : 800, maxHeight: isMobile ? '92vh' : '90vh',
        overflowY: 'auto', padding: '1.5rem',
        WebkitOverflowScrolling: 'touch'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <h2 style={{ fontFamily: '"DM Serif Display", Georgia, serif', fontSize: '1.3rem', fontWeight: 400, margin: 0 }}>
            {isNew ? 'New Template' : 'Edit Template'}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: ASHEN, minHeight: 44, minWidth: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>

        {error && <div style={{ padding: '0.5rem 0.75rem', background: '#fee2e2', color: '#991b1b', fontSize: '0.8rem', borderRadius: 8, marginBottom: '1rem' }}>{error}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
          {/* Left Column - Form */}
          <div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: ASHEN, marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Template Name</label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Booking Confirmation"
                style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #e5e2de', borderRadius: 8, fontSize: '16px', fontFamily: 'inherit' }} />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: ASHEN, marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Trigger</label>
              <select value={triggerType} onChange={e => setTriggerType(e.target.value)}
                style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #e5e2de', borderRadius: 8, fontSize: '16px', fontFamily: 'inherit', background: '#fff' }}>
                <option value="">Select trigger...</option>
                {Object.entries(triggerTypes).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: ASHEN, marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Timing</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <select value={timingUnit} onChange={e => setTimingUnit(e.target.value)}
                  style={{ flex: 1, minWidth: 120, padding: '0.6rem 0.75rem', border: '1px solid #e5e2de', borderRadius: 8, fontSize: '16px', fontFamily: 'inherit', background: '#fff' }}>
                  <option value="immediately">Immediately</option>
                  <option value="minutes">Minutes</option>
                  <option value="hours">Hours</option>
                  <option value="days">Days</option>
                </select>
                {timingUnit !== 'immediately' && (
                  <>
                    <input type="number" value={timingValue} onChange={e => setTimingValue(e.target.value)} min="0"
                      style={{ width: 70, padding: '0.6rem 0.75rem', border: '1px solid #e5e2de', borderRadius: 8, fontSize: '16px', fontFamily: 'inherit' }} />
                    <select value={timingDirection} onChange={e => setTimingDirection(e.target.value)}
                      style={{ flex: 1, minWidth: 100, padding: '0.6rem 0.75rem', border: '1px solid #e5e2de', borderRadius: 8, fontSize: '16px', fontFamily: 'inherit', background: '#fff' }}>
                      <option value="before">Before</option>
                      <option value="after">After</option>
                    </select>
                  </>
                )}
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: ASHEN, marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Studio</label>
              <select value={studioId} onChange={e => setStudioId(e.target.value)}
                style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #e5e2de', borderRadius: 8, fontSize: '16px', fontFamily: 'inherit', background: '#fff' }}>
                <option value="">All Studios</option>
                {studios.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.85rem' }}>
                <input type="checkbox" checked={autoSend} onChange={e => setAutoSend(e.target.checked)}
                  style={{ width: 20, height: 20, accentColor: GARNET }} />
                <span><strong>Auto-send</strong> — skip draft approval</span>
              </label>
            </div>
          </div>

          {/* Right Column - Body + Preview */}
          <div>
            <div style={{ marginBottom: '0.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: ASHEN, marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Message Body</label>
              {availableVars.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: '0.4rem' }}>
                  {availableVars.map(v => (
                    <button key={v} onClick={() => insertVariable(v)}
                      style={{ padding: '0.2rem 0.5rem', background: OYSTER, border: '1px solid #e5e2de', borderRadius: 4, fontSize: '0.7rem', cursor: 'pointer', color: GARNET, fontWeight: 500, fontFamily: 'monospace', minHeight: 32 }}>
                      {'{' + v + '}'}
                    </button>
                  ))}
                </div>
              )}
              <textarea id="template-body-input" value={body} onChange={e => setBody(e.target.value)}
                placeholder="Type your message here... Use {first_name} for variables"
                style={{ width: '100%', minHeight: 160, padding: '0.6rem 0.75rem', border: '1px solid #e5e2de', borderRadius: 8, fontSize: '16px', fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.5 }} />
            </div>

            {preview && (
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: ASHEN, marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Live Preview</label>
                <div style={{ background: '#e8f5ef', borderRadius: 12, padding: '0.75rem', fontSize: '0.85rem', lineHeight: 1.5, whiteSpace: 'pre-wrap', color: COAL, border: '1px solid #d1fae5' }}>
                  {preview}
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: '1.25rem', justifyContent: 'flex-end', flexDirection: isMobile ? 'column' : 'row' }}>
          <button onClick={onClose} style={{ padding: '0.7rem 1.25rem', background: '#f3f4f6', color: COAL, border: 'none', borderRadius: 8, fontSize: '0.9rem', cursor: 'pointer', minHeight: 44, order: isMobile ? 2 : 1 }}>Cancel</button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '0.7rem 1.25rem', background: GARNET, color: '#fff', border: 'none', borderRadius: 8, fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.4 : 1, minHeight: 44, order: isMobile ? 1 : 2 }}>
            {saving ? 'Saving...' : (isNew ? 'Create Template' : 'Save Changes')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Templates List ─────────────────────────────────────────────────
function TemplatesList() {
  const isMobile = useIsMobile();
  const [templates, setTemplates] = useState([]);
  const [triggerTypes, setTriggerTypes] = useState({});
  const [triggerVariables, setTriggerVariables] = useState({});
  const [studios, setStudios] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadTemplates = useCallback(async () => {
    try {
      const [tmplRes, studioRes] = await Promise.all([
        api.get('/templates'),
        api.get('/booking/studios')
      ]);
      setTemplates(tmplRes.data.templates);
      setTriggerTypes(tmplRes.data.trigger_types);
      setTriggerVariables(tmplRes.data.trigger_variables);
      setStudios(studioRes.data.studios || []);
    } catch (e) {
      console.error('Failed to load templates:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const handleToggle = async (templateId) => {
    try {
      await api.post(`/templates/${templateId}/toggle`);
      loadTemplates();
    } catch (e) {
      console.error('Toggle failed:', e);
    }
  };

  const handleDelete = async (templateId, templateName) => {
    if (!window.confirm(`Delete template "${templateName}"?`)) return;
    try {
      await api.delete(`/templates/${templateId}`);
      loadTemplates();
    } catch (e) {
      console.error('Delete failed:', e);
    }
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: ASHEN }}>Loading templates...</div>;

  return (
    <div style={{ padding: isMobile ? '1rem' : '1.25rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h3 style={{ fontFamily: '"DM Serif Display", Georgia, serif', fontSize: '1.1rem', fontWeight: 400, margin: 0 }}>Message Templates</h3>
          <p style={{ fontSize: '0.8rem', color: ASHEN, margin: '0.2rem 0 0' }}>{templates.length} template{templates.length !== 1 ? 's' : ''} · {templates.filter(t => t.is_active).length} active</p>
        </div>
        <button onClick={() => setEditing('new')}
          style={{ padding: '0.5rem 1rem', background: GARNET, color: '#fff', border: 'none', borderRadius: 8, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', minHeight: 44 }}>
          + New Template
        </button>
      </div>

      {templates.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem 1rem', color: ASHEN }}>
          <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>No templates yet</div>
          <p style={{ fontSize: '0.9rem' }}>Create your first message template to start automating follow-ups.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          {templates.map(t => (
            <div key={t.id} style={{
              background: '#fff', borderRadius: 12, padding: isMobile ? '0.85rem 1rem' : '1rem 1.25rem',
              border: '1px solid #e5e2de', opacity: t.is_active ? 1 : 0.6,
              transition: 'opacity 0.2s'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 8 : 0 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>{t.name}</span>
                    <TriggerBadge trigger={t.trigger_type} />
                    {t.auto_send && (
                      <span style={{ fontSize: '0.6rem', padding: '0.1rem 0.35rem', borderRadius: 6, background: '#e8f5ef', color: '#1a7a4c', fontWeight: 600 }}>Auto-send</span>
                    )}
                    {!t.is_active && (
                      <span style={{ fontSize: '0.6rem', padding: '0.1rem 0.35rem', borderRadius: 6, background: '#f3f4f6', color: ASHEN, fontWeight: 600 }}>Inactive</span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: ASHEN, marginBottom: 6 }}>
                    {t.timing_unit === 'immediately' ? 'Sends immediately' : `${t.timing_value} ${t.timing_unit} ${t.timing_direction}`}
                    {' · '}{t.studio_name}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: COAL, lineHeight: 1.4, whiteSpace: 'pre-wrap', maxHeight: 60, overflow: 'hidden' }}>
                    {t.body.substring(0, 150)}{t.body.length > 150 ? '...' : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap' }}>
                  <button onClick={() => handleToggle(t.id)} title={t.is_active ? 'Deactivate' : 'Activate'}
                    style={{ padding: '0.35rem 0.7rem', background: t.is_active ? '#fff3e6' : '#e8f5ef', color: t.is_active ? '#b35c00' : '#1a7a4c', border: 'none', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', minHeight: 36 }}>
                    {t.is_active ? 'Pause' : 'Activate'}
                  </button>
                  <button onClick={() => setEditing(t)}
                    style={{ padding: '0.35rem 0.7rem', background: '#f3f4f6', color: COAL, border: 'none', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', minHeight: 36 }}>
                    Edit
                  </button>
                  <button onClick={() => handleDelete(t.id, t.name)}
                    style={{ padding: '0.35rem 0.7rem', background: '#fce8e8', color: '#b32d2d', border: 'none', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', minHeight: 36 }}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <TemplateEditor
          template={editing === 'new' ? null : editing}
          triggerTypes={triggerTypes}
          triggerVariables={triggerVariables}
          studios={studios}
          onSave={() => { setEditing(null); loadTemplates(); }}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

// ─── Main Messages Page ─────────────────────────────────────────────
export default function Messages() {
  const isMobile = useIsMobile();
  const location = useLocation();
  const [, setStats] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('conversations');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const searchTimerRef = useRef(null);

  const loadConversations = useCallback(async () => {
    try {
      const [statsRes, convRes] = await Promise.all([
        api.get('/followups/stats'),
        api.get('/messages/conversations', { params: { filter } })
      ]);
      setStats(statsRes.data);
      setConversations(convRes.data.conversations);
    } catch (e) {
      console.error('Failed to load:', e);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  // Auto-select conversation when returning from customer profile
  useEffect(() => {
    const openId = location.state?.openCustomerId;
    if (openId) setSelectedCustomerId(openId);
  }, [location.state]);

  useEffect(() => {
    if (activeTab !== 'conversations') return;
    const interval = setInterval(loadConversations, 15000);
    return () => clearInterval(interval);
  }, [loadConversations, activeTab]);

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    const q = searchQuery.trim();
    if (q.length < 2) { setSearchResults([]); return; }
    searchTimerRef.current = setTimeout(async () => {
      try {
        const res = await api.get('/messages/search', { params: { q } });
        setSearchResults(res.data.conversations);
      } catch (e) {
        console.error('Search failed:', e);
      }
    }, 300);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [searchQuery]);

  const baseConversations = searchQuery.trim().length >= 2 ? searchResults : conversations;
  const displayedConversations = countryFilter === 'all'
    ? baseConversations
    : baseConversations.filter(c => (c.country || 'HK').toUpperCase() === countryFilter);

  // Mobile: show either conversation list or thread, not both
  const showThreadOnMobile = isMobile && selectedCustomerId;

  // Tell the tab bar to hide when viewing a thread on mobile
  useEffect(() => {
    if (window.__setHideTabBar) {
      window.__setHideTabBar(showThreadOnMobile);
    }
    return () => {
      if (window.__setHideTabBar) {
        window.__setHideTabBar(false);
      }
    };
  }, [showThreadOnMobile]);

  // Add CSS class to main-content for full-bleed messaging layout
  useEffect(() => {
    const mainContent = document.querySelector('.main-content');
    if (mainContent) {
      mainContent.classList.add('messages-active');
      if (showThreadOnMobile) {
        mainContent.classList.add('messages-thread');
      } else {
        mainContent.classList.remove('messages-thread');
      }
    }
    return () => {
      if (mainContent) {
        mainContent.classList.remove('messages-active');
        mainContent.classList.remove('messages-thread');
      }
    };
  }, [showThreadOnMobile]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: ASHEN }}>Loading messages...</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', background: '#fff' }}>
      {/* Tab Bar — hidden when viewing a thread on mobile */}
      {!(showThreadOnMobile) && (
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #e5e2de', background: '#fff', flexShrink: 0 }}>
          {[
            { key: 'conversations', label: 'Conversations', count: conversations.filter(c => c.needs_reply).length },
            { key: 'templates', label: 'Templates', count: 0, hideOnMobile: true }
          ].filter(tab => !(isMobile && tab.hideOnMobile)).map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              style={{
                flex: isMobile ? 1 : 'none',
                padding: '0.75rem 1.5rem', border: 'none', background: 'none', cursor: 'pointer',
                fontSize: '0.85rem', fontWeight: 600, color: activeTab === tab.key ? GARNET : ASHEN,
                borderBottom: activeTab === tab.key ? `2px solid ${GARNET}` : '2px solid transparent',
                transition: 'all 0.15s', minHeight: 48,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
              }}>
              {tab.label}
              {tab.count > 0 && (
                <span style={{
                  background: GARNET, color: '#fff', fontSize: '0.65rem', fontWeight: 700,
                  padding: '2px 6px', borderRadius: 10, minWidth: 18, textAlign: 'center'
                }}>{tab.count}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {activeTab === 'conversations' ? (
        isMobile ? (
          /* ===== MOBILE: Full-screen list OR full-screen thread ===== */
          showThreadOnMobile ? (
            <MessageThread
              key={selectedCustomerId}
              customerId={selectedCustomerId}
              onMessageChange={loadConversations}
              onBack={() => setSelectedCustomerId(null)}
              isMobile={true}
            />
          ) : (
            <ConversationList
              conversations={displayedConversations}
              selectedId={selectedCustomerId}
              onSelect={setSelectedCustomerId}
              filter={filter}
              onFilterChange={setFilter}
              countryFilter={countryFilter}
              onCountryFilterChange={setCountryFilter}
              isMobile={true}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
            />
          )
        ) : (
          /* ===== DESKTOP: Side-by-side layout ===== */
          <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <ConversationList
              conversations={displayedConversations}
              selectedId={selectedCustomerId}
              onSelect={setSelectedCustomerId}
              filter={filter}
              onFilterChange={setFilter}
              countryFilter={countryFilter}
              onCountryFilterChange={setCountryFilter}
              isMobile={false}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
            />
            {selectedCustomerId ? (
              <MessageThread
                key={selectedCustomerId}
                customerId={selectedCustomerId}
                onMessageChange={loadConversations}
                isMobile={false}
              />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: ASHEN, fontSize: '0.95rem' }}>
                Select a conversation to view messages
              </div>
            )}
          </div>
        )
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <TemplatesList />
        </div>
      )}
    </div>
  );
}